import React from 'react';
import { Star } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Permanent disclaimer shown on the StarRating (and reused by StarBadge).
 * The star rating scores *appearance / physical quality*, not deal economics.
 */
export const STAR_RATING_TOOLTIP =
  'Quality rating — not an investment rating. Low stars + high achievable stars is the value-add profile.';

type StarSize = 'sm' | 'md' | 'lg';

export interface StarRatingProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Fractional star value (0..max), quarter-fill rendered. */
  value: number;
  /** Optional confidence-range low bound (0..max) for the muted range band. */
  low?: number | null;
  /** Optional confidence-range high bound (0..max) for the muted range band. */
  high?: number | null;
  /** Star size. */
  size?: StarSize;
  /** Total number of stars. */
  max?: number;
  /** Render the muted low–high range band beneath the stars. */
  showRangeBand?: boolean;
  /** Render the numeric value (e.g. "3.5") to the right of the stars. */
  showValue?: boolean;
  /** Attach the permanent quality-vs-investment disclaimer tooltip. */
  showTooltip?: boolean;
  /** Override the tooltip text. */
  tooltip?: string;
}

const STAR_PX: Record<StarSize, number> = { sm: 14, md: 18, lg: 24 };
const GAP_CLASS: Record<StarSize, string> = { sm: 'gap-0.5', md: 'gap-0.5', lg: 'gap-1' };
const TEXT_CLASS: Record<StarSize, string> = { sm: 'text-xs', md: 'text-sm', lg: 'text-base' };
const BAND_HEIGHT: Record<StarSize, string> = { sm: 'h-1', md: 'h-1.5', lg: 'h-1.5' };

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
/** Trim trailing zeros: 3.5 → "3.5", 4 → "4", 3.25 → "3.25". */
const fmtStars = (n: number) => String(Math.round(n * 100) / 100);

/** Single star with a quarter-quantized gold fill overlaid on a muted base. */
function FractionalStar({ fillPct, px }: { fillPct: number; px: number }) {
  return (
    <span className="relative inline-block flex-shrink-0" style={{ width: px, height: px }}>
      <Star size={px} strokeWidth={1.5} className="absolute inset-0 text-gray-300 dark:text-gray-600 fill-gray-100 dark:fill-gray-800" />
      {fillPct > 0 && (
        <span
          className="absolute inset-y-0 left-0 overflow-hidden"
          style={{ width: `${fillPct}%` }}
        >
          <Star size={px} strokeWidth={1.5} className="text-accent-500 fill-accent-500" />
        </span>
      )}
    </span>
  );
}

/**
 * Fractional (quarter-fill) star display in Elevation accent gold (#D9A066 = accent-500),
 * deliberately distinct from the warning-yellow "favorite" Star used in the buy box.
 * Optionally renders a muted band for the confidence range (low–high).
 */
const StarRating = React.forwardRef<HTMLDivElement, StarRatingProps>(
  (
    {
      className,
      value,
      low,
      high,
      size = 'md',
      max = 5,
      showRangeBand = false,
      showValue = false,
      showTooltip = true,
      tooltip = STAR_RATING_TOOLTIP,
      ...props
    },
    ref
  ) => {
    const px = STAR_PX[size];
    const safeValue = clamp(value ?? 0, 0, max);

    const stars = Array.from({ length: max }, (_, i) => {
      const raw = clamp(safeValue - i, 0, 1); // 0..1 fill for this star
      const quarters = Math.round(raw * 4) / 4; // quantize to quarter-fill
      return quarters * 100;
    });

    const hasRange =
      showRangeBand &&
      low != null &&
      high != null &&
      high > low &&
      max > 0;
    const bandLeft = hasRange ? (clamp(low as number, 0, max) / max) * 100 : 0;
    const bandWidth = hasRange
      ? (clamp((high as number) - (low as number), 0, max) / max) * 100
      : 0;

    return (
      <div
        ref={ref}
        className={cn('group relative inline-flex items-center', TEXT_CLASS[size], className)}
        role="img"
        aria-label={`Quality rating ${fmtStars(safeValue)} out of ${max} stars`}
        title={showTooltip ? tooltip : undefined}
        {...props}
      >
        <div className="inline-flex flex-col">
          <div className={cn('flex items-center', GAP_CLASS[size])}>
            {stars.map((fillPct, i) => (
              <FractionalStar key={i} fillPct={fillPct} px={px} />
            ))}
          </div>
          {hasRange && (
            <div className={cn('relative mt-1 w-full rounded-full bg-gray-100 dark:bg-gray-800', BAND_HEIGHT[size])}>
              <div
                className="absolute inset-y-0 rounded-full bg-accent-200"
                style={{ left: `${bandLeft}%`, width: `${bandWidth}%` }}
              />
            </div>
          )}
        </div>

        {showValue && (
          <span className="ml-1.5 font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
            {fmtStars(safeValue)}
          </span>
        )}

        {showTooltip && (
          <span
            role="tooltip"
            className="pointer-events-none absolute bottom-full left-0 z-20 mb-1 hidden w-56 whitespace-normal rounded-md bg-gray-900 px-2 py-1 text-xs font-normal leading-snug text-white shadow-lg group-hover:block"
          >
            {tooltip}
          </span>
        )}
      </div>
    );
  }
);

StarRating.displayName = 'StarRating';

export { StarRating };
